import React, {
  CSSProperties,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';

import {
  Card,
  Col,
  Empty,
  Result,
  Row,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';

import { useGetUserDetailsQuery } from '../graphql/generated';
import { UserContext } from '../contexts/UserContext';
import { BuildingOutlined, NetworkOutlined } from '../components/MabelIcons';
import NetworkTag from '../components/networks/NetworkTag';

const { CheckableTag } = Tag;

const pageStyle: CSSProperties = {
  maxWidth: 1120,
  margin: '0 auto',
};

const profileCardStyle: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
};

const organizationHeaderStyle: CSSProperties = {
  alignItems: 'flex-start',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 24,
  justifyContent: 'space-between',
};

const iconPanelStyle: CSSProperties = {
  alignItems: 'center',
  background: '#f8fafc',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  display: 'flex',
  flex: '0 0 160px',
  height: 96,
  justifyContent: 'center',
};

const metaGridStyle: CSSProperties = {
  display: 'grid',
  gap: '18px 32px',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  marginTop: 24,
};

const networkCardBodyStyle: CSSProperties = {
  minHeight: 220,
};

const networkCardContentStyle: CSSProperties = {
  display: 'grid',
  gap: '18px 24px',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
};

const networkFieldStackStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
};

const getLabelValue = (label: string, value?: React.ReactNode) => {
  if (value === undefined || value === null || value === '') return null;

  return (
    <div>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <div style={{ fontSize: 16, marginTop: 2 }}>{value}</div>
    </div>
  );
};

const getNetworkLogo = (networkName: string, logo?: string | null) => {
  return (
    <div
      style={{
        ...iconPanelStyle,
        height: 84,
        width: 150,
      }}
    >
      {logo ? (
        <img
          alt={`${networkName} logo`}
          src={logo}
          style={{ maxHeight: 64, maxWidth: 124, objectFit: 'contain' }}
        />
      ) : (
        <NetworkOutlined style={{ color: '#3f7468', fontSize: 44 }} />
      )}
    </div>
  );
};

const ClientOrganizationProfilePage: React.FC = () => {
  const { user } = useContext(UserContext);
  const [selectedAcoIds, setSelectedAcoIds] = useState<string[]>([]);
  const { data, loading, error } = useGetUserDetailsQuery({
    variables: { user_id: user?.user_id },
  });
  const organization = data?.users_by_pk?.client_organization;
  const organizationNetworks = organization?.organization_networks;
  const activeOrganizationNetworks = useMemo(() => {
    return (
      organizationNetworks
        ?.filter((organizationNetwork) => {
          return (
            !organizationNetwork.is_archived &&
            !organizationNetwork.network.is_archived
          );
        })
        .sort((a, b) =>
          a.network.network_name.localeCompare(b.network.network_name)
        ) || []
    );
  }, [organizationNetworks]);
  const acoIds = useMemo(() => {
    return Array.from(
      new Set(
        activeOrganizationNetworks
          .map(({ network }) => network.dc_id)
          .filter((acoId): acoId is string => Boolean(acoId))
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [activeOrganizationNetworks]);
  const selectedAcoIdSet = useMemo(() => {
    return new Set(selectedAcoIds);
  }, [selectedAcoIds]);
  const filteredOrganizationNetworks = useMemo(() => {
    if (!acoIds.length) return activeOrganizationNetworks;

    return activeOrganizationNetworks.filter(({ network }) => {
      return Boolean(network.dc_id && selectedAcoIdSet.has(network.dc_id));
    });
  }, [acoIds.length, activeOrganizationNetworks, selectedAcoIdSet]);

  useEffect(() => {
    setSelectedAcoIds(acoIds);
  }, [acoIds]);

  const handleAcoFilterChange = (acoId: string, checked: boolean) => {
    setSelectedAcoIds((currentAcoIds) => {
      if (checked) {
        return [...currentAcoIds, acoId].sort((a, b) => a.localeCompare(b));
      }

      return currentAcoIds.filter((currentAcoId) => currentAcoId !== acoId);
    });
  };

  if (loading) return <Skeleton />;
  if (error)
    return (
      <Result
        status="error"
        title="There was an error retrieving organization details"
        subTitle={error?.message}
      />
    );
  if (!organization)
    return (
      <Result
        status="warning"
        title="No organization profile found"
        subTitle="Your account is not attached to a client organization."
      />
    );

  return (
    <>
      <Helmet>
        <title>Organization Settings | Mabel</title>
      </Helmet>
      <div style={pageStyle}>
        <Typography.Title level={3}>Organization Settings</Typography.Title>
        <Typography.Paragraph>
          If any of this information is incorrect or needs to be updated, please
          contact <a href="mailto:support@getmabel.com">support@getmabel.com</a>
        </Typography.Paragraph>
        <Card bodyStyle={{ padding: 32 }} style={profileCardStyle}>
          <div style={organizationHeaderStyle}>
            <div>
              <Typography.Text type="secondary">
                Organization Name
              </Typography.Text>
              <Typography.Title
                level={2}
                style={{ marginBottom: 18, marginTop: 2 }}
              >
                {organization.organization_name}
              </Typography.Title>
              <Typography.Text type="secondary">
                Organization Description
              </Typography.Text>
              <Typography.Paragraph
                style={{
                  fontSize: 17,
                  marginBottom: 18,
                  marginTop: 2,
                  maxWidth: 720,
                }}
                type={
                  organization.organization_description
                    ? undefined
                    : 'secondary'
                }
              >
                {organization.organization_description ||
                  'No description provided.'}
              </Typography.Paragraph>
            </div>
            <div style={iconPanelStyle}>
              <BuildingOutlined style={{ color: '#3f7468', fontSize: 48 }} />
            </div>
          </div>
          <div style={metaGridStyle}>
            {getLabelValue(
              'Networks',
              activeOrganizationNetworks.length ? (
                <Space wrap>
                  {activeOrganizationNetworks.map(({ network }) => (
                    <NetworkTag key={network.network_id} network={network} />
                  ))}
                </Space>
              ) : (
                <Typography.Text type="secondary">None listed</Typography.Text>
              )
            )}
            {getLabelValue(
              'ACOs',
              acoIds.length ? (
                <Space wrap>
                  {acoIds.map((acoId) => (
                    <Tag key={acoId}>{acoId}</Tag>
                  ))}
                </Space>
              ) : (
                <Typography.Text type="secondary">None listed</Typography.Text>
              )
            )}
            {getLabelValue('Organization ID', organization.organization_id)}
          </div>
        </Card>

        <Space
          align="center"
          style={{
            justifyContent: 'space-between',
            marginTop: 32,
            width: '100%',
          }}
        >
          <Space align="center" wrap>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Networks
            </Typography.Title>
            {acoIds.length ? (
              <Space wrap>
                {acoIds.map((acoId) => (
                  <CheckableTag
                    checked={selectedAcoIdSet.has(acoId)}
                    key={acoId}
                    onChange={(checked) => {
                      handleAcoFilterChange(acoId, checked);
                    }}
                  >
                    {acoId}
                  </CheckableTag>
                ))}
              </Space>
            ) : null}
          </Space>
          <Typography.Text type="secondary">
            {filteredOrganizationNetworks.length} active
          </Typography.Text>
        </Space>

        {filteredOrganizationNetworks.length ? (
          <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
            {filteredOrganizationNetworks.map(({ network }) => (
              <Col key={network.network_id} lg={12} md={24} sm={24} xs={24}>
                <Card
                  bodyStyle={networkCardBodyStyle}
                  style={{
                    ...profileCardStyle,
                    height: '100%',
                  }}
                >
                  <div style={networkCardContentStyle}>
                    <div style={networkFieldStackStyle}>
                      <div>
                        <Typography.Text type="secondary">
                          Network Name
                        </Typography.Text>
                        <Typography.Title
                          level={4}
                          style={{ marginBottom: 0, marginTop: 2 }}
                        >
                          {network.network_name}
                        </Typography.Title>
                      </div>

                      <div>
                        <Typography.Text type="secondary">
                          Network Description
                        </Typography.Text>
                        <Typography.Paragraph
                          style={{ marginBottom: 0, marginTop: 2 }}
                          type={
                            network.network_description
                              ? undefined
                              : 'secondary'
                          }
                        >
                          {network.network_description ||
                            'No network description provided.'}
                        </Typography.Paragraph>
                      </div>

                      {getLabelValue(
                        'ACO ID',
                        network.dc_id ? <Tag>{network.dc_id}</Tag> : null
                      )}
                      {getLabelValue('Network ID', network.network_id)}
                    </div>

                    <div>
                      <Typography.Text type="secondary">Logo</Typography.Text>
                      <div style={{ marginTop: 4 }}>
                        {getNetworkLogo(
                          network.network_name,
                          network.network_logo
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Card style={{ ...profileCardStyle, marginTop: 16 }}>
            <Empty
              description={
                activeOrganizationNetworks.length
                  ? 'No networks match the selected ACO filters.'
                  : 'No active networks are attached to this organization.'
              }
            />
          </Card>
        )}
      </div>
    </>
  );
};

export default ClientOrganizationProfilePage;
